import i18n, { t } from "i18next";

import { devWarn } from "@/util/dev.mjs";

export function i18nString(en: string) {
  return {
    // TODO: make sure key exists in translation files or do sth more
    transKey: en,
    englishFallbackText: en,
  };
}

export function getLocale(): string {
  return (
    i18n.language ||
    i18n.options.fallbackLng?.[0]?.[0] ||
    i18n.options.fallbackLng?.[0] ||
    i18n.options.fallbackLng
  );
}

export function getLocaleString(n: number, options?: Intl.NumberFormatOptions) {
  const locale = getLocale();

  return typeof n === "number" || !Number.isNaN(parseFloat(n))
    ? n.toLocaleString(locale, options)
    : "-";
}

export const getLocaleRate = (
  a: number,
  b: number,
  options?: Intl.NumberFormatOptions,
) => {
  a = a || 0;
  b = b || 1;

  try {
    const v = (a / b).toLocaleString(
      getLocale(),
      options || {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      },
    );

    return v;
  } catch (e) {
    return (a / b).toFixed(0);
  }
};

export const toDecimal = (x: number | bigint) =>
  new Intl.NumberFormat(getLocale(), { style: "decimal" }).format(x);

export const toPercent = (x: number | bigint) =>
  new Intl.NumberFormat(getLocale(), { style: "percent" }).format(x);

export const getDecimalSeparator = () => {
  return Intl.NumberFormat(getLocale())
    .formatToParts(1.1)
    .find((part) => part.type === "decimal").value;
};

export const formatToPercent = (
  language = getLocale(),
  n: number,
  { min = 0, max = 1 } = {},
) =>
  typeof n === "number" || !Number.isNaN(parseFloat(n))
    ? new Intl.NumberFormat(language, {
        style: "percent",
        minimumFractionDigits: min,
        maximumFractionDigits: max,
      }).format(typeof n === "number" ? n : parseFloat(n))
    : "-%";

type Notation = "standard" | "scientific" | "engineering" | "compact";
export const formatToFixedNumber = (
  language: string,
  n: number,
  maximumFractionDigits = 2,
  notation: Notation = "standard",
) =>
  typeof n === "number" || !Number.isNaN(parseFloat(n))
    ? n.toLocaleString(language, { maximumFractionDigits, notation })
    : "-";

export const formatToMeter = (language: string, n: number) =>
  n.toLocaleString(language, { style: "unit", unit: "meter" });

export function formatOrdinalNumber(lng = i18n.language, n = 0) {
  // Sanity check: sometimes we get data passed in directly from places where there is no type safety.
  if (typeof n !== "number") {
    devWarn(`Non-number passed into formatOrdinalNumber: ${n}`);
    return "";
  }

  const pluralRules = new Intl.PluralRules(lng, { type: "ordinal" });

  const formatParams = { notation: "standard" };
  const ordinal = pluralRules.select(n);
  switch (ordinal) {
    case "zero":
      return t("common:ordinal_zero", "{{n, number}}th", { n, formatParams });
    case "one":
      return t("common:ordinal_one", "{{n, number}}st", { n, formatParams });
    case "two":
      return t("common:ordinal_two", "{{n, number}}nd", { n, formatParams });
    case "few":
      return t("common:ordinal_few", "{{n, number}}rd", { n, formatParams });
    case "many":
      return t("common:ordinal_many", "{{n, number}}th", { n, formatParams });
    default: // other
      return t("common:ordinal_other", "{{n, number}}th", { n, formatParams });
  }
}

export function formatNumberWithUnit(n: number, language = i18n.language) {
  if (typeof n !== "number") {
    return "-";
  }

  return Intl.NumberFormat(language, {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(n);
}

export function formatNumberWithSign(lng, value) {
  return value || value === 0
    ? Intl.NumberFormat(lng, {
        style: "decimal",
        signDisplay: "always",
      }).format(value)
    : "-";
}

export function formatDateTime(
  lng: string,
  value: Date,
  options?: Intl.DateTimeFormatOptions,
) {
  return new Intl.DateTimeFormat(lng, options).format(value);
}

export function formatCurrency(
  lng: string | string[],
  value: number | bigint,
  currency: string,
) {
  return new Intl.NumberFormat(lng, {
    style: "currency",
    maximumFractionDigits: Number.isInteger(Number(value)) ? 0 : 2,
    currency: currency ?? "usd",
  }).format(value);
}

export function formatParticipantCount(lng: string, count: number) {
  if (count === null || count === undefined) return "-";
  return new Intl.NumberFormat(lng, { notation: "compact" }).format(count);
}

export function formatList(
  lng: string | string[],
  items: string[],
  opts: Intl.ListFormatOptions = { style: "short", type: "unit" },
) {
  return new Intl.ListFormat(lng, opts).format(items);
}
